import Link from '@/routing/Link'
import { usePathname, useRouter } from '@/routing/navigation'
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowLeft, ArrowRight, ChevronRight, Info, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@design-system/components/alert";
import { SessionExpirySnackbar } from "../../components/SessionExpirySnackbar";
import { Button } from "@design-system/components/button";
import { Skeleton } from "@design-system/components/skeleton";
import { Text } from "@/components/ui/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ImportStepHeader } from "../../components/ImportStepHeader";
import { ImportActionBar } from "../../components/ImportActionBar";
import { DiscardImportSessionDialog } from "../../components/DiscardImportSessionDialog";
import { useAudienceImportSessionMutations } from "../../hooks/useAudienceImportSession";
import { useContextMaterialsProjection } from "../../hooks/useContextMaterialsProjection";
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import {
  deriveImportTargetRoute,
  navigateToImportRoute,
  warmImportRouteCaches,
} from '../../lib/import-session-route-handoff'
import { useImportWizardContext } from "../../context/ImportWizardContext";
import { ContextMaterialsStepCard } from "../context-materials/components/ContextMaterialsStepCard";
import { FileUploadZone } from "@/components/upload/FileUploadZone";
import { toast } from "sonner";
import type { ContextMaterialFileItem } from "@digital-twins/shared-types";

function formatTimestamp(value: string | null | undefined) {
  if (!value) return "Unavailable";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ContextMaterialsStep() {
  const { t } = useTranslation("app");
  const router = useRouter();
  const pathname = usePathname();
  const { sessionId, snapshot, goToStep } = useImportWizardContext();
  const workbookAssignmentRoute = snapshot?.links?.workbookAssignment;
  const {
    projection,
    error: projectionError,
    isLoading: isProjectionLoading,
  } = useContextMaterialsProjection(sessionId);
  const fallbackMachineVersion =
    typeof snapshot?.machineVersion === 'number'
      ? snapshot.machineVersion
      : typeof projection?.machineVersion === 'number'
        ? projection.machineVersion
        : undefined;
  const {
    error,
    isMutating,
    isEnteringContextMaterials,
    isCompletingOrSkippingContextMaterials,
    isUploadingContextMaterial,
    isRemovingContextMaterial,
    discard,
    isDiscarding,
    enterContextMaterials,
    completeOrSkipContextMaterials,
    uploadContextMaterialFile,
    removeContextMaterialFile,
    listContextMaterialFiles,
  } = useAudienceImportSessionMutations(sessionId, {
    fallbackMachineVersion,
  });
  const [files, setFiles] = useState<ContextMaterialFileItem[]>([]);
  const [enterError, setEnterError] = useState<Error | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const skipOrCompleteInitiatedRef = useRef(false);
  const contextMaterials = projection?.contextMaterials;
  const explicitEnterSatisfied =
    contextMaterials?.enteredAt != null &&
    contextMaterials?.draftStorageId != null;
  const contextState =
    contextMaterials?.status === "completed"
      ? "completed"
      : explicitEnterSatisfied
        ? "active"
        : "not_entered";

  const expiresAtMs = projection?.expiresAt
    ? new Date(projection.expiresAt).getTime()
    : null;
  const isExpiringSoon =
    expiresAtMs != null &&
    expiresAtMs > now &&
    expiresAtMs - now <= 15 * 60 * 1000;

  const hasAutoEntered = useRef(false);

  useEffect(() => {
    setEnterError(null);
    setActionError(null);
    hasAutoEntered.current = false;
    skipOrCompleteInitiatedRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (contextState !== "completed") return;
    if (isProjectionLoading) return;
    if (
      !projection ||
      typeof fallbackMachineVersion !== 'number' ||
      isEnteringContextMaterials ||
      isMutating ||
      enterError ||
      skipOrCompleteInitiatedRef.current
    ) {
      return;
    }
    if (hasAutoEntered.current) {
      return;
    }
    hasAutoEntered.current = true;
    setFiles([]);
    enterContextMaterials().catch((err) => setEnterError(err as Error));
  }, [
    contextState,
    enterContextMaterials,
    enterError,
    fallbackMachineVersion,
    isEnteringContextMaterials,
    isMutating,
    isProjectionLoading,
    projection,
  ]);

  const handleEnterStep = useCallback(async () => {
    setEnterError(null);
    setActionError(null);
    try {
      await enterContextMaterials();
    } catch (err) {
      setEnterError(err as Error);
    }
  }, [enterContextMaterials]);

  const handleSkip = useCallback(async () => {
    if (isMutating) {
      return;
    }
    skipOrCompleteInitiatedRef.current = true;
    setActionError(null);
    try {
      const ack = await completeOrSkipContextMaterials('skip')
      // Prefer demographics over segmentation when routeAck omits flowSnapshot.route —
      // otherwise firstNonEmptyRoute can jump past Define demographics.
      const navigationRoute = deriveImportTargetRoute({
        sessionId,
        routeAck: ack,
        snapshot,
        fallbackRoutes: [snapshot?.links?.requiredFields, snapshot?.links?.segmentation],
      })
      await warmImportRouteCaches({
        sessionId,
        targetRoute: navigationRoute,
        ack,
      })
      navigateToImportRoute({
        sessionId,
        sourceRoute: pathname,
        targetRoute: navigationRoute,
        router,
        trigger: 'skip_context_materials',
      })
    } catch (err) {
      skipOrCompleteInitiatedRef.current = false;
      if (
        err instanceof Error &&
        err.message === "Another import session action is already in progress."
      ) {
        toast.info("Please wait for the current action to finish.");
        return;
      }
      const message = getApiErrorMessage(
        err,
        "Failed to skip the context materials step.",
      );
      setActionError(message);
      toast.error(message);
    }
  }, [completeOrSkipContextMaterials, isMutating, pathname, router, sessionId, snapshot]);

  const handleComplete = useCallback(async () => {
    if (isMutating) {
      return;
    }
    skipOrCompleteInitiatedRef.current = true;
    setActionError(null);
    try {
      const ack = await completeOrSkipContextMaterials('complete');
      // Prefer demographics over segmentation when routeAck omits flowSnapshot.route —
      // otherwise firstNonEmptyRoute can jump past Define demographics.
      const navigationRoute = deriveImportTargetRoute({
        sessionId,
        routeAck: ack,
        snapshot,
        fallbackRoutes: [snapshot?.links?.requiredFields, snapshot?.links?.segmentation],
      });
      await warmImportRouteCaches({
        sessionId,
        targetRoute: navigationRoute,
        ack,
      });
      navigateToImportRoute({
        sessionId,
        sourceRoute: pathname,
        targetRoute: navigationRoute,
        router,
        trigger: 'complete_context_materials',
      });
    } catch (err) {
      skipOrCompleteInitiatedRef.current = false;
      if (
        err instanceof Error &&
        err.message === 'Another import session action is already in progress.'
      ) {
        toast.info('Please wait for the current action to finish.');
        return;
      }
      const message = getApiErrorMessage(
        err,
        'Failed to continue to the next step.',
      );
      setActionError(message);
      toast.error(message);
    }
  }, [completeOrSkipContextMaterials, isMutating, pathname, router, sessionId, snapshot]);

  async function handleDiscard() {
    try {
      await discard();
      setDiscardDialogOpen(false);
      router.push("/audiences/new");
    } catch {}
  }

  const isStepActionBusy =
    isMutating ||
    isEnteringContextMaterials ||
    isCompletingOrSkippingContextMaterials ||
    isUploadingContextMaterial ||
    isRemovingContextMaterial;

  if (isProjectionLoading && !projection) {
    return (
      <>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-[280px] rounded-2xl" />
      </>
    );
  }

  if (error || projectionError || !projection) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="-ml-3 self-start text-muted-foreground hover:text-foreground"
        >
          <Link href="/audiences/new">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("audienceCreate.startOver")}
          </Link>
        </Button>
        <ImportStepHeader
          title={t("audienceCreate.contextUnavailable")}
          helper={t("audienceCreate.sessionLoadFailed")}
        />
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {(error ?? projectionError) instanceof Error
              ? getApiErrorMessage(
                  error ?? projectionError,
                  t("audienceCreate.sessionExpiredOrDiscarded"),
                )
              : t("audienceCreate.sessionExpiredOrDiscarded")}
          </AlertDescription>
        </Alert>
      </>
    );
  }

  return (
    <>
      <DiscardImportSessionDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={handleDiscard}
        isPending={isDiscarding}
      />

      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
            {t("audienceCreate.steps.context")}
          </h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={t("audienceCreate.contextMoreInfo")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                {t("audienceCreate.contextTooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Text size="lg" color="muted" className="leading-[1.6] tracking-[-0.02em]">
          {t("audienceCreate.contextSubtitle")}
        </Text>
      </header>

      {isExpiringSoon ? (
        <SessionExpirySnackbar
          message={`This temporary session expires after inactivity. Viewing or interacting with the flow extends the working window until ${formatTimestamp(projection?.expiresAt)}.`}
        />
      ) : null}

      {actionError ? (
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      {enterError ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-border/70 bg-card/95 p-12 shadow-sm">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <Text size="sm" weight="medium" color="muted">
            {getApiErrorMessage(
              enterError,
              "Failed to initialize the context materials step.",
            )}
          </Text>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleEnterStep()}
              disabled={isEnteringContextMaterials}
            >
              {isEnteringContextMaterials ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Retrying…
                </>
              ) : (
                "Retry"
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void handleSkip()}
              disabled={isStepActionBusy}
            >
              {t("audienceCreate.skipThisStep")}
            </Button>
          </div>
        </div>
      ) : isEnteringContextMaterials ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-border/70 bg-card/95 p-12 shadow-sm">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <Text size="sm" weight="medium" color="muted">
            Initializing context materials step…
          </Text>
        </div>
      ) : contextState === "not_entered" ? (
        <div className="rounded-xl border-dashed-6">
          <FileUploadZone
            variant="context"
            accept=".txt,.csv,.html,.htm,.json,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            multiple
            disabled={isStepActionBusy}
            onFilesSelected={(fileList) => {
              const toUpload = Array.from(fileList);
              if (toUpload.length === 0) return;
              void (async () => {
                try {
                  await enterContextMaterials();
                  let latestFiles: Array<ContextMaterialFileItem> = [];
                  for (const file of toUpload) {
                    const res = await uploadContextMaterialFile(file);
                    latestFiles = res.files ?? latestFiles;
                  }
                  // Seed list from mutation responses so the card shows files immediately
                  // (GET …/files?minimal=true can briefly lag behind the write).
                  if (latestFiles.length > 0) {
                    setFiles(latestFiles);
                  }
                } catch (err) {
                  setEnterError(err as Error);
                }
              })();
            }}
          />
        </div>
      ) : (
        <ContextMaterialsStepCard
          files={files}
          onFilesChange={setFiles}
          onUpload={uploadContextMaterialFile}
          onRemove={removeContextMaterialFile}
          isUploading={isUploadingContextMaterial}
          isRemoving={isRemovingContextMaterial}
          refreshToken={projection?.machineVersion}
          listFiles={listContextMaterialFiles}
        />
      )}

      <ImportActionBar
        backLabel={t("audienceCreate.steps.upload")}
        onBack={() => goToStep("upload")}
        nextLabel={
          contextState === "active" || contextState === "completed"
            ? t("audienceCreate.steps.identity")
            : t("audienceCreate.skipStep")
        }
        onNext={
          contextState === "active" || contextState === "completed"
            ? () => void handleComplete()
            : () => void handleSkip()
        }
        nextDisabled={isEnteringContextMaterials || isCompletingOrSkippingContextMaterials}
        nextLoading={isCompletingOrSkippingContextMaterials}
        nextLoadingLabel={t("audienceCreate.pleaseWait")}
      />
    </>
  );
}
